/**
 * EditorShell - Iframe Mode Implementation
 *
 * This component renders the visual editor for iframe mode where the customer's
 * website loads in an isolated iframe. Communication happens via postMessage bridge.
 *
 * UPDATED: Now renders within the existing dashboard layout instead of a fixed overlay.
 * Uses EditorModeContext to communicate with FilterBar for editor-specific controls.
 */

import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EditorProvider,
  ParentBridge,
  instructionTextValue,
  useEditor,
  type EditorParams,
} from "@humblytics/editor-core";
import { IframePreview } from "./IframePreview";
import { Button } from "@/components/ui/button";
import { useSidebarSafe } from "@/components/ui/sidebar";
import { toast } from "sonner";
import {
  AISidebarProvider,
  useAISidebar,
  useAISidebarSafe,
} from "@/lib/contexts/AISidebarContext";
import {
  AIPageContextProvider,
  useAIPageContextSafe,
} from "@/lib/contexts/AIPageContext";
// UnifiedEditorPanelParent removed - editor UI now lives in AI sidebar's Editor tab
import { AIVaryModal } from "./AIVaryModal";
import { EditorTopBar } from "./EditorTopBar";
import { EditorChangesPanel } from "./EditorChangesPanel";
import { LaunchSettingsPanel } from "./LaunchSettingsPanel";
import { EditorElementPanel } from "./EditorElementPanel";
import { getPagesBreakdown } from "@/lib/api/external-api";
import { PAGE_TRAFFIC_DAYS, pageTitle } from "@/components/experiments/newTestModel";
import { suggestedExperimentGoals } from "@/lib/config/experimentContext";
import {
  newElementDraft,
  readDraftVariantInstructions,
  readSplitTestDraft,
  writeSplitTestDraft,
  type DraftSplitTestState,
} from "@/components/experiments/splitTestDraft";
import { useLaunchElementTest } from "@/components/experiments/useLaunchElementTest";
import { getAbTestDraft } from "@/lib/api/api";
import {
  logExperimentDegradation,
  reportExperimentFailure,
} from "@/lib/observability/experiments";
import type { ComponentInstruction } from "./utils/instructionTypes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAbTestDraft, useUpdateAbTestDraft } from "@/hooks/useExperiments";
import { useEditorModeSafe, VIEWPORT_SIZES, type ViewportSize, type VariantId } from "@/lib/contexts/EditorModeContext";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  CheckmarkCircle01Icon,
  Clock02Icon,
  Link01Icon,
  Refresh01Icon,
} from "@hugeicons/core-free-icons"

interface EditorShellProps {
  pageUrl: string;
  draftId: string;
  authToken: string;
  onBack?: () => void;
  onFinishEditing?: () => void;
  /**
   * Element to start on (Heatmaps "Test this element" → `?element=`). Used
   * only when the draft has no saved changes: the page scrolls to it, the
   * runtime selects it and the Editor tab opens on it.
   */
  initialSelector?: string | null;
  /** Site the test runs on; the Launch tab reads its page traffic and creates the test there */
  propertyId?: string;
  /** "/workspace/:id" or "/p/:slug", for the experiments list after launch */
  basePath?: string;
}

type PanelTab = "element" | "changes" | "launch";

/**
 * Bridge event handler - runs inside EditorProvider to sync bridge events with EditorContext
 */
function BridgeEventHandler({ bridge }: { bridge: ParentBridge | null }) {
  const { dispatch } = useEditor();
  const aiSidebar = useAISidebar();
  const aiPageContext = useAIPageContextSafe();

  // Use refs to avoid stale closures — the handler is registered once per bridge
  const aiSidebarRef = useRef(aiSidebar);
  useEffect(() => { aiSidebarRef.current = aiSidebar; }, [aiSidebar]);

  const aiPageContextRef = useRef(aiPageContext);
  useEffect(() => { aiPageContextRef.current = aiPageContext; }, [aiPageContext]);

  useEffect(() => {
    if (!bridge) return;

    // Handle element click intent from iframe — parent is the authoritative decision-maker
    const handleElementClickedIntent = (payload: {
      selector: string;
      bounds: DOMRect;
      tagName: string;
      textContent: string;
      computedStyles: Record<string, string>;
      src?: string;
      alt?: string;
    }) => {
      dispatch({
        type: "SET_SELECTED_ELEMENT",
        element: null, // In iframe mode, the element lives in the iframe's DOM
      });

      const sidebar = aiSidebarRef.current;

      // 1. If there's a current element with uncommitted previews, revert first
      if (sidebar.editorElement && sidebar.editorElement.selector !== payload.selector) {
        bridge.send({ type: 'REVERT_PREVIEWS', payload: { selector: sidebar.editorElement.selector } } as any);
      }

      // 2. Tell runtime to highlight the new element (parent is authoritative)
      bridge.send({ type: 'SELECTION_SET', payload: { selector: payload.selector } } as any);

      // 3. Update parent state (opens editor tab)
      // Note: production iframe script may not send computedStyles — default to empty
      sidebar.openEditorTab({
        selector: payload.selector,
        tagName: payload.tagName,
        textContent: payload.textContent,
        computedStyles: payload.computedStyles || {},
        src: payload.src,
        alt: payload.alt,
      });

      // 4. Sync selected element to AI context so AI knows what's selected
      const aiCtx = aiPageContextRef.current;
      if (aiCtx) {
        aiCtx.setSplitTestEditorContext({
          selectedElement: {
            selector: payload.selector,
            tagName: payload.tagName,
            textContent: payload.textContent,
          },
        });
      }
    };

    // Listen for both new intent AND legacy message (backward compat)
    bridge.on("ELEMENT_CLICKED_INTENT", handleElementClickedIntent);
    bridge.on("ELEMENT_SELECTED", handleElementClickedIntent);

    return () => {
      bridge.off("ELEMENT_CLICKED_INTENT", handleElementClickedIntent);
      bridge.off("ELEMENT_SELECTED", handleElementClickedIntent);
    };
  }, [bridge, dispatch]);

  return null; // This component only handles side effects
}

/**
 * Undo/Redo interceptor - In iframe mode, sends commands to iframe instead of operating locally
 */
function UndoRedoInterceptor({ bridge }: { bridge: ParentBridge | null }) {
  const { state, canUndo, canRedo } = useEditor();

  useEffect(() => {
    if (!bridge || state.deploymentMode !== "iframe") return;

    // Override keyboard shortcuts to send to iframe
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform);
      const isModifierPressed = isMac ? e.metaKey : e.ctrlKey;

      if (isModifierPressed && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        bridge.send({ type: "UNDO" });
        return;
      }

      if (isModifierPressed && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        bridge.send({ type: "REDO" });
        return;
      }
    };

    // Capture phase to intercept before EditorUI's handlers
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [bridge, state.deploymentMode, canUndo, canRedo]);

  return null;
}

type DraftInstruction = { selector: string; op: string; value: unknown };

function readLocalDraftInstructions(draftId: string, variantId: string): unknown[] {
  try {
    const raw = localStorage.getItem(`humblytics_draft_instructions_${draftId}`);
    if (!raw) return [];
    const data = JSON.parse(raw);
    const list = data?.variants?.[variantId]?.instructions;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeLocalDraftInstructions(draftId: string, variantId: string, instructions: DraftInstruction[]): boolean {
  const storageKey = `humblytics_draft_instructions_${draftId}`;
  try {
    const existingData = localStorage.getItem(storageKey);
    const data = existingData ? JSON.parse(existingData) : { variants: {} };
    if (!data.variants || typeof data.variants !== "object") data.variants = {};
    data.variants[variantId] = {
      instructions,
      updatedAt: new Date().toISOString(),
    };
    data.updatedAt = new Date().toISOString();
    localStorage.setItem(storageKey, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}

/** When this draft's saved changes were last written, by any tab. */
function readLocalDraftUpdatedAt(draftId: string): string | null {
  try {
    const raw = localStorage.getItem(`humblytics_draft_instructions_${draftId}`);
    return raw ? (JSON.parse(raw)?.updatedAt ?? null) : null;
  } catch {
    return null;
  }
}

function normalizeDraftInstructions(list: unknown[]): DraftInstruction[] {
  const out: DraftInstruction[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const row = item as { selector?: unknown; op?: unknown; operation?: unknown; value?: unknown };
    const selector = typeof row.selector === "string" ? row.selector : "";
    const op = typeof row.op === "string" ? row.op : typeof row.operation === "string" ? row.operation : "";
    if (!selector || !op) continue;
    out.push({ selector, op, value: row.value });
  }
  return out;
}

/**
 * Inner component that has access to AISidebarContext
 */
function EditorShellInner({
  pageUrl,
  draftId,
  authToken,
  onBack,
  initialSelector,
  propertyId = "",
  basePath = "",
}: EditorShellProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [bridge, setBridge] = useState<ParentBridge | null>(null);
  const bridgeRef = useRef<ParentBridge | null>(null); // Ref for callbacks to access current bridge
  // Canonical post-redirect URL resolved by the embed probe (defaults to the
  // stored pageUrl until/unless the backend returns a finalUrl). Held in a ref
  // so load/open callbacks read the latest value without a stale closure.
  const resolvedPageUrlRef = useRef<string>(pageUrl);
  const [isReady, setIsReady] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  // Mirror of isReady for timer closures (avoids stale-closure reads).
  const isReadyRef = useRef(false);
  // Silent REQUEST_READY resend interval + single connection deadline.
  const readyRetryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectionDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [iframeWidth, setIframeWidth] = useState<number | null>(null); // null = 100%
  const [iframeHeight, setIframeHeight] = useState<number | null>(null); // null = default
  const [viewport, setViewport] = useState<ViewportSize>({ name: "Best Fit", width: 0, height: 0 }); // Best Fit default
  // Default editing target is Variant B — Control (A) is the baseline.
  const [currentVariantId, setCurrentVariantId] = useState<VariantId>('B');
  const [variants, setVariants] = useState([
    { id: 'A' as VariantId, name: 'Control', instructionCount: 0 },
    { id: 'B' as VariantId, name: 'Variant B', instructionCount: 0 },
  ]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Unified connection-failure state. `null` = still connecting / connected.
  // 'embed-blocked'   → the customer page can't be embedded (CSP/XFO/never loaded)
  // 'handshake-failed'→ the page loaded but the editor script never connected
  const [fallbackCause, setFallbackCause] = useState<null | 'embed-blocked' | 'handshake-failed'>(null);
  const [userDismissedOverlay, setUserDismissedOverlay] = useState(false);
  const [isEditingInNewTab, setIsEditingInNewTab] = useState(false);
  const [newTabChangeCount, setNewTabChangeCount] = useState(0);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [trackedInstructions, setTrackedInstructions] = useState<ComponentInstruction[]>([]);
  const [showReviewBeforeSave, setShowReviewBeforeSave] = useState(false);
  // Two tabs on one draft: the last save wins, so a stale tab must not
  // autosave over a newer list. `syncedAtRef` is the version this tab has.
  const syncedAtRef = useRef<string | null>(null);
  const [changedElsewhere, setChangedElsewhere] = useState(false);
  // Set when this iframe session has already replayed saved instructions.
  const hydratedForRef = useRef<string | null>(null);
  // Local revert/remove list to paint after the iframe reloads, instead of the saved draft.
  const pendingReplayRef = useRef<DraftInstruction[] | null>(null);

  // AI Optimize modal state - when iframe requests AI variation
  const [aiOptimizeElementData, setAiOptimizeElementData] = useState<{
    selector: string;
    tagName: string;
    textContent: string;
    computedStyles: Record<string, string>;
  } | null>(null);

  // Mutation to save instructions to backend. PUT replaces the variant list; [] clears it.
  const updateDraftMutation = useUpdateAbTestDraft();
  const queryClient = useQueryClient();
  const { data: draftQuery, isFetched: isDraftFetched } = useAbTestDraft(draftId);

  const persistDraftInstructions = useCallback((variantId: string, instructions: DraftInstruction[]) => {
    if (!writeLocalDraftInstructions(draftId, variantId, instructions)) {
      reportExperimentFailure("editor_save", new Error("local draft write failed"), {}, { draftId });
    }
    syncedAtRef.current = readLocalDraftUpdatedAt(draftId);
    queryClient.setQueryData(
      ["ab-test-draft", draftId],
      (prev: { success?: boolean; data?: { testId?: string; variant?: string; instructions?: unknown[]; updatedAt?: string } | null } | undefined) => ({
        success: true,
        data: {
          ...(prev?.data ?? {}),
          testId: prev?.data?.testId ?? draftId,
          variant: variantId,
          instructions,
          updatedAt: new Date().toISOString(),
        },
      }),
    );
    return updateDraftMutation.mutateAsync({
      draftId,
      variant: variantId,
      instructions: instructions as unknown as ComponentInstruction[],
    });
  }, [draftId, queryClient, updateDraftMutation]);
  const persistDraftRef = useRef(persistDraftInstructions);
  useEffect(() => {
    persistDraftRef.current = persistDraftInstructions;
  }, [persistDraftInstructions]);

  // Guarded back navigation - checks for unsaved changes
  const guardedBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      onBack?.();
    }
  }, [hasUnsavedChanges, onBack]);

  // Prevent accidental navigation with unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Keep ref in sync with state
  useEffect(() => {
    bridgeRef.current = bridge;
  }, [bridge]);

  // Get AI sidebar context - now correctly inside the provider
  const aiSidebar = useAISidebar();

  // Get left nav sidebar (shadcn) — null in standalone mode (no SidebarProvider)
  const leftSidebar = useSidebarSafe();
  const leftSidebarPrevOpenRef = useRef(leftSidebar?.open ?? true);

  // On mount: collapse left sidebar, open right sidebar with Editor tab
  // On unmount: restore left sidebar, reset editor mode
  useEffect(() => {
    if (leftSidebar) {
      // Remember previous left sidebar state so we can restore it
      leftSidebarPrevOpenRef.current = leftSidebar.open;
      // Collapse left nav sidebar to maximize iframe space
      leftSidebar.setOpen(false);
    }
    // Register editor mode + auto-open right sidebar with Editor tab
    aiSidebar.setIsEditorMode(true);
    aiSidebar.setActiveTab('editor');
    aiSidebar.open();

    return () => {
      aiSidebar.setIsEditorMode(false);
      // Collapse the panel back to the FAB on every exit path (finish-save,
      // back button, route change) — it's no longer actionable once the
      // editor is gone, so leaving it open just blocks the page behind it.
      aiSidebar.close();
      // Restore left sidebar to its previous state
      if (leftSidebar) {
        leftSidebar.setOpen(leftSidebarPrevOpenRef.current);
      }
    };
  }, []);

  // ── Paper "02 Editor" / "03 Launch": right panel tabs, the test draft, launch ──
  const [panelTab, setPanelTab] = useState<PanelTab>("element");
  const pagePath = useMemo(() => {
    try { return new URL(pageUrl).pathname || "/"; } catch { return pageUrl; }
  }, [pageUrl]);
  const pageHost = useMemo(() => {
    try { return new URL(pageUrl).hostname.replace(/^www\./, ""); } catch { return ""; }
  }, [pageUrl]);
  const suggestedGoals = useMemo(() => suggestedExperimentGoals(null, null), []);
  const [testDraft, setTestDraft] = useState<DraftSplitTestState>(() => {
    const stored = readSplitTestDraft(draftId);
    if (stored) return stored.propertyId || !propertyId ? stored : { ...stored, propertyId };
    // Opened without the page picker (old link, Heatmaps): start a draft for this page.
    const fresh = newElementDraft({
      draftId,
      propertyId,
      pagePath,
      testGoal: suggestedGoals[0],
      experimentName: `${pageTitle(pagePath)} test`,
    });
    writeSplitTestDraft(fresh);
    return fresh;
  });
  const updateTestDraft = useCallback((patch: Partial<DraftSplitTestState>) => {
    setTestDraft((prev) => {
      const next = { ...prev, ...patch };
      writeSplitTestDraft(next);
      return next;
    });
  }, []);

  // Same query (and cache key) as the page picker: page traffic, last 30 days.
  const { data: pageTraffic } = useQuery({
    queryKey: ["planner-page-traffic", propertyId],
    queryFn: () => getPagesBreakdown("day_PAST_30_D", propertyId, authToken, 50),
    enabled: Boolean(propertyId && authToken),
    staleTime: 5 * 60 * 1000,
  });
  const pageDaily = useMemo(() => {
    const norm = (p: string) => p.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
    const row = (pageTraffic?.data ?? []).find((r) => r.page && norm(r.page) === norm(pagePath));
    return row?.unique_visitors ? Math.max(1, Math.round(row.unique_visitors / PAGE_TRAFFIC_DAYS)) : null;
  }, [pageTraffic, pagePath]);

  const { launch, isLaunching, atLimit } = useLaunchElementTest({
    propertyId: testDraft.selectedPropertyForTest || testDraft.propertyId || propertyId,
    basePath,
  });
  // What the next CHANGES_SAVED is for: leaving, an autosave, or launching.
  const afterSaveRef = useRef<"back" | "stay" | "launch">("back");
  const savedVersionB = useCallback((): unknown[] => {
    const local = readDraftVariantInstructions(draftId, "B");
    if (local.length) return local;
    const saved = draftQuery?.data;
    return saved?.variant === "B" && Array.isArray(saved.instructions) ? saved.instructions : [];
  }, [draftId, draftQuery]);
  const launchRef = useRef<(saved: unknown[] | null) => Promise<void>>(async () => {});
  useEffect(() => {
    launchRef.current = async (saved) => {
      await launch(testDraft, saved ?? savedVersionB());
    };
  }, [launch, testDraft, savedVersionB]);
  const changesOnB = Math.max(
    currentVariantId === "B" ? trackedInstructions.length : 0,
    readDraftVariantInstructions(draftId, "B").length,
  );
  useEffect(() => {
    const key = `humblytics_draft_instructions_${draftId}`;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || !e.newValue) return;
      try {
        const at = JSON.parse(e.newValue)?.updatedAt ?? null;
        if (at && at !== syncedAtRef.current) setChangedElsewhere(true);
      } catch {
        // ignore unreadable entries
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [draftId]);

  const handleLaunch = useCallback(() => {
    if (changedElsewhere) {
      toast.error("This draft changed in another tab. Reload to launch the latest version.");
      return;
    }
    const current = bridgeRef.current;
    if (hasUnsavedChanges && current) {
      afterSaveRef.current = "launch";
      current.send({ type: "SAVE_CHANGES" });
      return;
    }
    void launchRef.current(null);
  }, [hasUnsavedChanges, changedElsewhere]);

  // Changes save on their own a moment after the last edit ("Draft saved").
  useEffect(() => {
    if (!hasUnsavedChanges || !isReady || changedElsewhere) return;
    const timer = setTimeout(() => {
      const current = bridgeRef.current;
      if (!current || afterSaveRef.current === "launch") return;
      afterSaveRef.current = "stay";
      current.send({ type: "SAVE_CHANGES" });
    }, 1500);
    return () => clearTimeout(timer);
  }, [hasUnsavedChanges, isReady, trackedInstructions.length, changedElsewhere]);

  // Clicking an element on the page opens the Element tab on it. The first
  // time an unchanged element is picked, its text is what Control shows.
  const controlTextBySelector = useRef(new Map<string, string>());
  useEffect(() => {
    const picked = aiSidebar.editorElement;
    if (!picked) return;
    setPanelTab("element");
    const known = controlTextBySelector.current.has(picked.selector);
    const changed = trackedInstructions.some((i) => i.selector === picked.selector);
    if (!known && !changed && picked.textContent.trim()) {
      controlTextBySelector.current.set(picked.selector, picked.textContent.trim());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSidebar.editorElement]);

  // Sync bridge to sidebar context when it changes
  useEffect(() => {
    aiSidebar.setEditorBridge(bridge);
  }, [bridge]);

  // Get editor mode context to communicate with FilterBar
  const editorModeContext = useEditorModeSafe();

  // Get AI page context to update capabilities when bridge is ready
  const aiPageContext = useAIPageContextSafe();

  // Track if we've set capabilities to avoid infinite loop
  // (setCapabilities causes context to change, which would re-trigger this effect)
  const capabilitiesSetRef = useRef(false);

  // Update AI context when bridge becomes ready
  // This sets both capabilities AND split test editor context on the OUTER context
  // so that AISidebar (rendered in DashboardLayout) can access it
  useEffect(() => {
    if (bridge && isReady && aiPageContext && !capabilitiesSetRef.current) {
      capabilitiesSetRef.current = true;
      
      // Set capabilities for AI to apply changes
      aiPageContext.setCapabilities({
        canApplyInstructions: true,
        onApplyInstruction: (message: { type: string; payload: any }) => {
          // AI now sends messages with specific types: APPLY_TEXT_CHANGE, APPLY_STYLE_CHANGES, etc.
          bridge.send(message as any);

          // Track applied instruction in AI context
          const currentCtx = aiPageContext.context.splitTestEditorContext;
          const currentInstructions = currentCtx?.appliedInstructions || [];
          aiPageContext.setSplitTestEditorContext({
            appliedInstructions: [
              ...currentInstructions,
              {
                selector: message.payload?.selector || 'unknown',
                operation: message.type,
                description: `Applied ${message.type} via AI`,
              },
            ],
          });
        },
      });

      // Set split test editor context so AI knows what page is being edited
      aiPageContext.setSplitTestEditorContext({
        mode: "nocode_visual_editor",
        targetPageUrl: pageUrl,
        draftId: draftId,
      });

      // Request page structure from iframe for AI context
      bridge.send({ type: 'GET_PAGE_STRUCTURE' } as any);
      bridge.on('PAGE_STRUCTURE', (payload: any) => {
        aiPageContext.setSplitTestEditorContext({ pageStructure: payload });
      });
    }
    // Reset ref if bridge disconnects
    if (!bridge || !isReady) {
      capabilitiesSetRef.current = false;
    }
  }, [bridge, isReady, pageUrl, draftId]); // Removed aiPageContext from deps to prevent infinite loop
  
  // Clean up split test editor context when unmounting
  useEffect(() => {
    return () => {
      if (aiPageContext) {
        aiPageContext.clearContext();
      }
    };
  }, []); // Only run on unmount

  // Keep the ref mirror in sync so timer closures read the latest ready state.
  useEffect(() => {
    isReadyRef.current = isReady;
  }, [isReady]);

  // Clear both connection timers (silent retry interval + deadline).
  const clearConnectionTimers = useCallback(() => {
    if (readyRetryIntervalRef.current) {
      clearInterval(readyRetryIntervalRef.current);
      readyRetryIntervalRef.current = null;
    }
    if (connectionDeadlineRef.current) {
      clearTimeout(connectionDeadlineRef.current);
      connectionDeadlineRef.current = null;
    }
  }, []);

  // Single connection deadline: silently resend REQUEST_READY on a fixed
  // cadence, then transition to the unified fallback if the handshake hasn't
  // completed within the deadline. Replaces the old overlapping timers
  // (10s overlay toast + 2s kickoff + 1/2/4/8/16s backoff).
  const startConnectionDeadline = useCallback((bridgeInstance: ParentBridge) => {
    clearConnectionTimers();
    const RETRY_INTERVAL = 1200; // silent resend cadence
    const DEADLINE = 8000;       // give up after ~8s and show the fallback

    readyRetryIntervalRef.current = setInterval(() => {
      if (isReadyRef.current) {
        clearConnectionTimers();
        return;
      }
      try {
        bridgeInstance.send({ type: "REQUEST_READY" } as any);
      } catch {
        // Swallow — the deadline below owns the give-up path.
      }
    }, RETRY_INTERVAL);

    connectionDeadlineRef.current = setTimeout(() => {
      clearConnectionTimers();
      if (isReadyRef.current) return;
      logExperimentDegradation("editor_connection_timeout", {}, {
        draftId,
        targetUrl: pageUrl,
        reason: "handshake_timeout",
        expectedOrigin: bridgeInstance.currentOrigin,
        lastRejectedOrigin: bridgeInstance.lastRejectedOrigin ?? undefined,
      });
      setShowLoadingOverlay(false);
      setIsReady(false);
      setFallbackCause("handshake-failed");
    }, DEADLINE);
  }, [clearConnectionTimers, draftId, pageUrl]);

  // Cleanup timers on unmount.
  useEffect(() => () => clearConnectionTimers(), [clearConnectionTimers]);

  // Build iframe URL with editor params
  const iframeUrl = (() => {
    try {
      const url = new URL(pageUrl);
      url.searchParams.set("hmblEditor", "1");
      url.searchParams.set("testId", draftId);
      url.searchParams.set("authToken", authToken);
      url.searchParams.set("testMode", "1");
      url.searchParams.set("experimental", "true");
      return url.toString();
    } catch {
      return `${pageUrl}?hmblEditor=1&testId=${draftId}&authToken=${authToken}&testMode=1&experimental=true`;
    }
  })();

  // Adopt the canonical post-redirect URL from the embed probe. Feeds the
  // bridge's expected origin and the new-tab fallback so both track the URL the
  // iframe actually loaded. Stable identity — reads/writes via the ref.
  const handleResolvedUrl = useCallback((finalUrl: string) => {
    resolvedPageUrlRef.current = finalUrl;
  }, []);

  // Initialize bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    hydratedForRef.current = null;
    isReadyRef.current = false;
    setIsReady(false);
    if (!iframeRef.current?.contentWindow) {
      reportExperimentFailure(
        "editor_init",
        new Error("Iframe contentWindow not available"),
        {},
        { draftId },
      );
      return;
    }

    try {
      // Prefer the canonical (post-redirect) URL when the probe resolved one,
      // so the bridge's expected origin matches the iframe's actual origin.
      const effectiveUrl = resolvedPageUrlRef.current || pageUrl;
      // Handle relative URLs (like /test-iframe-content.html) by using current origin
      const iframeOrigin = effectiveUrl.startsWith("/")
        ? window.location.origin
        : new URL(effectiveUrl).origin;

      const newBridge = new ParentBridge(iframeRef.current, iframeOrigin);

      // Listen for READY message
      newBridge.on("READY", () => {
        clearConnectionTimers();
        isReadyRef.current = true;
        setIsReady(true);
        setShowLoadingOverlay(false);
        setFallbackCause(null);
      });

      // Begin the single connection deadline (silent retries + give-up).
      startConnectionDeadline(newBridge);

      // Listen for connection lost
      newBridge.on("CONNECTION_LOST", () => {
        logExperimentDegradation("editor_connection_lost", {}, {
          draftId,
          targetUrl: pageUrl,
        });
        setIsReady(false);
        toast.error("Connection Lost", {
          description: "The editor connection was interrupted. Please refresh.",
        });
      });

      // Listen for STATE_CHANGED to track undo/redo state
      newBridge.on("STATE_CHANGED", (payload: {
        hasUnsavedChanges: boolean;
        instructionCount: number;
        historyIndex: number;
        historyLength: number;
      }) => {
        console.log('[HMBL Shell] STATE_CHANGED ←', payload);
        setCanUndo(payload.historyIndex >= 0);
        setCanRedo(payload.historyIndex < payload.historyLength - 1);
        setHasUnsavedChanges(payload.hasUnsavedChanges);
        // Keep trackedInstructions in sync with iframe state —
        // trims the list when undo removes instructions.
        setTrackedInstructions(prev =>
          payload.instructionCount < prev.length
            ? prev.slice(0, payload.instructionCount)
            : prev
        );
      });

      // OPEN_EDITOR / CLOSE_EDITOR removed — element editing now handled via sidebar Editor tab

      // Listen for REQUEST_AI_VARY from iframe (AI Optimize button in HoverActions)
      newBridge.on(
        "REQUEST_AI_VARY",
        (payload: { element: { selector: string; tagName: string; textContent: string; computedStyles: Record<string, string> } }) => {
          setAiOptimizeElementData(payload.element);
        }
      );

      // Listen for INSTRUCTION_ADDED from iframe — track instructions in parent state
      newBridge.on(
        "INSTRUCTION_ADDED",
        (payload: { selector: string; op: string; value: any }) => {
          const newInstruction: ComponentInstruction = {
            id: crypto.randomUUID(),
            selector: payload.selector,
            operation: payload.op as any,
            value: payload.value,
            timestamp: new Date().toISOString(),
          };
          setTrackedInstructions(prev => [...prev, newInstruction]);
        }
      );

      // Listen for CHANGES_SAVED from iframe - replace the saved list, including [].
      newBridge.on(
        "CHANGES_SAVED",
        async (payload: { variantId: string; instructions: any[] }) => {
          const quiet = afterSaveRef.current === "stay";
          const latest = readLocalDraftUpdatedAt(draftId);
          if (syncedAtRef.current && latest && latest > syncedAtRef.current) {
            // Another tab saved this draft after this one loaded it: writing now
            // would drop that tab's changes. Stop and ask for a reload.
            afterSaveRef.current = "back";
            setChangedElsewhere(true);
            if (!quiet) toast.error("This draft changed in another tab. Reload to get the latest before saving or launching.");
            return;
          }
          let savedList: DraftInstruction[] | null = null;
          if (Array.isArray(payload.instructions)) {
            const instructions = normalizeDraftInstructions(payload.instructions);
            savedList = instructions;
            // The page script keeps its own unsaved flag after a save; the saved
            // list is the truth for the count, the Changes tab and "Draft saved".
            setHasUnsavedChanges(false);
            setTrackedInstructions((prev) =>
              instructions.map((instruction, index) => ({
                id: prev[index]?.selector === instruction.selector ? prev[index].id : crypto.randomUUID(),
                selector: instruction.selector,
                operation: instruction.op as ComponentInstruction["operation"],
                value: instruction.value as ComponentInstruction["value"],
                timestamp: prev[index]?.timestamp ?? new Date().toISOString(),
              })),
            );
            try {
              await persistDraftRef.current(payload.variantId, instructions);
              if (!quiet) toast.success(
                instructions.length === 0
                  ? "Cleared saved changes"
                  : `Saved ${instructions.length} change(s) to server`,
              );
            } catch {
              // Reported as an Issue by useUpdateAbTestDraft; the local copy survives.
              toast.success(
                instructions.length === 0
                  ? "Cleared changes locally"
                  : `Saved ${instructions.length} change(s) locally`,
                { description: "Server save failed. Changes saved to browser storage." },
              );
            }
          }

          // What the save was for: launching from the Launch tab, staying, or leaving.
          const next = afterSaveRef.current;
          afterSaveRef.current = "back";
          if (next === "launch") {
            void launchRef.current(payload.variantId === "B" ? savedList : null);
          } else if (next === "back") {
            onBack?.();
          }
        }
      );

      // Page structure now comes from Firecrawl via analyzePage tool, not the iframe

      setBridge(newBridge);
    } catch (error) {
      reportExperimentFailure("editor_init", error, {}, { draftId });
      toast.error("Failed to initialize editor", {
        description: "Please try refreshing the page.",
      });
    }
  }, [pageUrl, draftId, clearConnectionTimers, startConnectionDeadline]);

  // Paint every saved change onto the pristine page. HYDRATE updates iframe state.
  // APPLY_TEXT_CHANGE with replay also sets the text: older iframe scripts ignore HYDRATE.
  // replay must not append an instruction (see APPLY_TEXT_CHANGE handler).
  const paintDraftInstructions = useCallback((current: ParentBridge, instructions: DraftInstruction[]) => {
    current.send({ type: "HYDRATE_INSTRUCTIONS", payload: { instructions } });
    const selectors: string[] = [];
    for (const instruction of instructions) {
      if (!instruction.selector) continue;
      selectors.push(instruction.selector);
      if (instruction.op === "text") {
        const text = instructionTextValue(instruction.value);
        if (text == null) continue;
        current.send({
          type: "APPLY_TEXT_CHANGE",
          payload: { selector: instruction.selector, text, replay: true },
        });
      } else if (instruction.op === "css" && instruction.value && typeof instruction.value === "object") {
        const raw = instruction.value as Record<string, unknown>;
        const styles =
          raw.properties && typeof raw.properties === "object"
            ? (raw.properties as Record<string, unknown>)
            : raw;
        for (const [property, value] of Object.entries(styles)) {
          if (property === "properties" || typeof value !== "string") continue;
          current.send({
            type: "APPLY_STYLE_PREVIEW",
            payload: { selector: instruction.selector, property, value },
          });
        }
      }
    }
    if (selectors.length === 0) return;
    current.send({ type: "HIGHLIGHT_ELEMENTS", payload: { selectors } });
    current.send({ type: "SCROLL_TO_ELEMENT", payload: { selector: selectors[0] } });
    current.send({ type: "SELECTION_SET", payload: { selector: selectors[0] } });
  }, []);

  const syncInstructions = useCallback((next: ComponentInstruction[]) => {
    const draftInstructions: DraftInstruction[] = next.map((instruction) => ({
      selector: instruction.selector,
      op: instruction.operation,
      value: instruction.value,
    }));
    setTrackedInstructions(next);
    pendingReplayRef.current = draftInstructions;
    hydratedForRef.current = null;
    isReadyRef.current = false;
    setIsReady(false);
    void persistDraftInstructions(currentVariantId, draftInstructions);
    const frame = iframeRef.current;
    if (!frame) return;
    // Same URL still reloads the pristine page. A new query param would hit the customer site.
    // eslint-disable-next-line no-self-assign
    frame.src = frame.src;
  }, [currentVariantId, persistDraftInstructions]);

  useEffect(() => {
    if (!isReady || !bridge || !isDraftFetched) return;
    if (hydratedForRef.current === draftId) return;

    if (pendingReplayRef.current) {
      const instructions = pendingReplayRef.current;
      pendingReplayRef.current = null;
      hydratedForRef.current = draftId;
      syncedAtRef.current = readLocalDraftUpdatedAt(draftId);
      if (instructions.length > 0) paintDraftInstructions(bridge, instructions);
      return;
    }

    const draft = draftQuery?.data;
    const savedVariant = draft?.variant;
    if (savedVariant && savedVariant !== currentVariantId) return;

    const fromApi =
      savedVariant === currentVariantId &&
      Array.isArray(draft?.instructions) &&
      draft.instructions.length > 0
        ? draft.instructions
        : null;
    const instructions = normalizeDraftInstructions(
      (fromApi ?? readLocalDraftInstructions(draftId, currentVariantId)) as unknown[],
    );

    hydratedForRef.current = draftId;
    syncedAtRef.current = readLocalDraftUpdatedAt(draftId);
    setTrackedInstructions(
      instructions.map((instruction) => ({
        id: crypto.randomUUID(),
        selector: instruction.selector,
        operation: instruction.op as ComponentInstruction["operation"],
        value: instruction.value as ComponentInstruction["value"],
        timestamp: new Date().toISOString(),
      })),
    );
    if (instructions.length === 0) {
      // Pre-target the linked element with the same messages a saved draft uses.
      // The runtime selects on SELECTION_SET but does not echo a click intent,
      // so the parent opens the Editor tab itself (tag/text fill in on click).
      if (initialSelector) {
        bridge.send({ type: "SCROLL_TO_ELEMENT", payload: { selector: initialSelector } } as any);
        bridge.send({ type: "SELECTION_SET", payload: { selector: initialSelector } } as any);
        aiSidebar.openEditorTab({ selector: initialSelector, tagName: "", textContent: "", computedStyles: {} });
      }
      return;
    }
    paintDraftInstructions(bridge, instructions);
    // aiSidebar is read once per hydrate; listing it would re-run on every sidebar change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, bridge, isDraftFetched, draftQuery, draftId, currentVariantId, paintDraftInstructions, initialSelector]);

  // Cleanup bridge on unmount
  useEffect(() => {
    return () => {
      if (bridge) {
        bridge.destroy();
      }
    };
  }, [bridge]);

  // Handle viewport change from FilterBar
  const handleViewportChange = useCallback((newViewport: ViewportSize) => {
    setViewport(newViewport);
    // If width is 0 (Best Fit), set to null to use 100% width
    setIframeWidth(newViewport.width > 0 ? newViewport.width : null);
    setIframeHeight(newViewport.height > 0 ? newViewport.height : null);
  }, []);

  // Handle variant switch from FilterBar
  const handleVariantSwitch = useCallback((variantId: string) => {
    setCurrentVariantId(variantId as VariantId);
    const currentBridge = bridgeRef.current;
    if (currentBridge) {
      currentBridge.send({ type: "SWITCH_VARIANT", payload: { variantId } });
    }
  }, []);

  // Handle variant create from FilterBar
  const handleVariantCreate = useCallback((variantId: VariantId) => {
    setVariants(prev => [...prev, { id: variantId, name: `Variant ${variantId}`, instructionCount: 0 }]);
  }, []);

  // Handle undo from FilterBar
  const handleUndo = useCallback(() => {
    const currentBridge = bridgeRef.current;
    console.log('[HMBL Shell] handleUndo — bridge exists?', !!currentBridge, 'canUndo:', canUndo);
    if (currentBridge) {
      currentBridge.send({ type: "UNDO" });
    }
  }, [canUndo]);

  // Handle redo from FilterBar
  const handleRedo = useCallback(() => {
    const currentBridge = bridgeRef.current;
    console.log('[HMBL Shell] handleRedo — bridge exists?', !!currentBridge, 'canRedo:', canRedo);
    if (currentBridge) {
      currentBridge.send({ type: "REDO" });
    }
  }, [canRedo]);

  // Handle finish editing
  const handleFinishEditing = useCallback(() => {
    const currentBridge = bridgeRef.current;
    if (currentBridge) {
      currentBridge.send({ type: "SAVE_CHANGES" });
      // Navigation will happen in CHANGES_SAVED handler
    } else {
      // No bridge, just navigate
      onBack?.();
    }
  }, [onBack]);

  // Save and go back (used from unsaved dialog)
  const handleSaveAndGoBack = useCallback(() => {
    setShowUnsavedDialog(false);
    // Show review sheet briefly, then save
    if (trackedInstructions.length > 0) {
      setShowReviewBeforeSave(true);
    } else {
      handleFinishEditing();
    }
  }, [trackedInstructions.length, handleFinishEditing]);

  // Confirm save from review sheet
  const handleConfirmSaveFromReview = useCallback(() => {
    setShowReviewBeforeSave(false);
    const currentBridge = bridgeRef.current;
    if (currentBridge) {
      currentBridge.send({ type: "SAVE_CHANGES" });
    } else {
      onBack?.();
    }
  }, [onBack]);

  // Open editor in new window as fallback when connection has issues (e.g. CSP blocks iframe).
  // Opens the raw customer site URL with editor params so the script runs directly on their page.
  // Changes saved from the editor on the live site (another origin): keep the
  // local copy, the tracked list and the count in step with the server draft.
  const adoptSavedList = useCallback((variantId: string, list: DraftInstruction[]) => {
    writeLocalDraftInstructions(draftId, variantId, list);
    syncedAtRef.current = readLocalDraftUpdatedAt(draftId);
    setNewTabChangeCount(list.length);
    if (variantId === currentVariantId) {
      setTrackedInstructions(
        list.map((instruction) => ({
          id: crypto.randomUUID(),
          selector: instruction.selector,
          operation: instruction.op as ComponentInstruction["operation"],
          value: instruction.value as ComponentInstruction["value"],
          timestamp: new Date().toISOString(),
        })),
      );
    }
    queryClient.setQueryData(["ab-test-draft", draftId], { success: true, data: { testId: draftId, variant: variantId, instructions: list, updatedAt: new Date().toISOString() } });
  }, [draftId, currentVariantId, queryClient]);
  const syncFromServer = useCallback(async () => {
    try {
      const fresh = await getAbTestDraft(draftId, authToken);
      const data = (fresh as { data?: { variant?: string; instructions?: unknown[] } } | null)?.data;
      if (data?.variant && Array.isArray(data.instructions)) {
        adoptSavedList(data.variant, normalizeDraftInstructions(data.instructions));
      }
    } catch {
      // Offline or expired: the opener message path still applies.
    }
  }, [draftId, authToken, adoptSavedList]);

  const handleOpenInNewWindow = useCallback(() => {
    // The stored URL can already carry the iframe's editor params; start clean
    // so the live-site editor gets each one once.
    const base = new URL(resolvedPageUrlRef.current || pageUrl);
    for (const key of ["hmblEditor", "testId", "authToken", "testMode", "experimental"]) base.searchParams.delete(key);
    const baseUrl = base.toString();
    const editorUrl = `${baseUrl}${base.search ? '&' : '?'}hmblEditor=1&testId=${draftId}&authToken=${authToken}&testMode=1&experimental=true`;
    // No "noopener" so the new tab can postMessage back via window.opener
    const newWindow = window.open(editorUrl, "_blank");
    if (newWindow) {
      setShowLoadingOverlay(false);
      setIsEditingInNewTab(true);
    } else {
      toast.error("Failed to open new window", {
        description: "Please check your browser popup blocker settings.",
      });
    }
  }, [pageUrl, draftId, authToken]);

  // Listen for changes saved from the new tab — via localStorage storage events
  // and via window.opener.postMessage
  useEffect(() => {
    if (!isEditingInNewTab) return;
    const storageKey = `humblytics_draft_instructions_${draftId}`;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          const count = Object.values(data.variants || {}).reduce(
            (acc: number, v: any) => acc + (v.instructions?.length ?? 0),
            0
          );
          setNewTabChangeCount(count as number);
        } catch {
          setNewTabChangeCount(0);
        }
      }
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === 'CHANGES_SAVED' && Array.isArray(e.data.payload?.instructions)) {
        // The editor on the live site saved (it also PUTs the draft itself):
        // adopt its list so Changes, the count and Launch use it.
        adoptSavedList(String(e.data.payload.variantId || "B"), normalizeDraftInstructions(e.data.payload.instructions));
      }
    };
    // Backup when the message is lost: re-read the draft whenever this tab regains focus.
    const handleFocus = () => { void syncFromServer(); };
    window.addEventListener('focus', handleFocus);

    window.addEventListener('storage', handleStorage);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingInNewTab, draftId]);

  // Register "open in new tab" fallback callback on sidebar context
  useEffect(() => {
    aiSidebar.setOnOpenInNewTab(handleOpenInNewWindow);
    return () => aiSidebar.setOnOpenInNewTab(null);
  }, [handleOpenInNewWindow]);

  // Handle iframe hard block (CSP, timeout, or error from IframePreview) —
  // the customer page can't be embedded at all.
  const handleIframeBlocked = useCallback((reason: 'csp' | 'timeout' | 'error') => {
    logExperimentDegradation("editor_connection_timeout", {}, {
      draftId,
      targetUrl: pageUrl,
      reason: `iframe_${reason}`,
    });
    setFallbackCause("embed-blocked");
    setShowLoadingOverlay(false);
    clearConnectionTimers();
  }, [draftId, pageUrl, clearConnectionTimers]);

  // Manual retry from the fallback panel — re-arm the deadline and show the
  // connecting spinner again.
  const handleRetryConnection = useCallback(() => {
    const currentBridge = bridgeRef.current;
    if (!currentBridge) return;
    setFallbackCause(null);
    setUserDismissedOverlay(false);
    setShowLoadingOverlay(true);
    currentBridge.send({ type: "REQUEST_READY" } as any);
    startConnectionDeadline(currentBridge);
  }, [startConnectionDeadline]);

  // Activate editor mode in the layout context (for FilterBar)
  // This effect runs after all callbacks are defined
  useEffect(() => {
    if (!editorModeContext) return;
    
    editorModeContext.activateEditorMode({
      initialViewport: viewport,
      initialVariantId: currentVariantId,
      initialVariants: variants,
      onViewportChange: handleViewportChange,
      onVariantSwitch: handleVariantSwitch,
      onVariantCreate: handleVariantCreate,
      onUndo: handleUndo,
      onRedo: handleRedo,
      onBack: guardedBack,
      onFinishEditing: handleFinishEditing,
      onOpenAISidebar: () => aiSidebar.open(),
    });

    return () => {
      editorModeContext.deactivateEditorMode();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount - callbacks are stable via refs

  // Sync local state changes to editor mode context
  useEffect(() => {
    if (!editorModeContext?.state.isEditorMode) return;
    
    editorModeContext.updateEditorState({
      canUndo,
      canRedo,
      hasUnsavedChanges,
      variants,
      currentVariantId,
      viewport,
    });
  }, [canUndo, canRedo, hasUnsavedChanges, variants, currentVariantId, viewport, editorModeContext]);

  // Editor params for EditorProvider (matching what iframe receives)
  // IMPORTANT: Use bridgeRef.current to always get the current bridge value
  // (avoids stale closure issues since params are captured at mount time)
  const editorParams: EditorParams = {
    testId: draftId,
    authToken: authToken,
    testMode: true,
    deploymentMode: "iframe",
    parentOrigin: window.location.origin,
    onOpenAISidebar: () => aiSidebar.open(), // Pass callback to open AI sidebar
    // In iframe mode, undo/redo should send commands to iframe
    onUndo: handleUndo,
    onRedo: handleRedo,
    onSwitchVariant: handleVariantSwitch,
    onFinishEditing: handleFinishEditing,
  };

  return (
    <EditorProvider params={editorParams}>
        {/* Bridge event handler - syncs iframe events with EditorContext */}
        <BridgeEventHandler bridge={bridge} />

        {/* Undo/Redo interceptor - sends commands to iframe in iframe mode */}
        <UndoRedoInterceptor bridge={bridge} />

        {/* Viewport is managed locally via handleViewportChange - no observer needed */}

        {/* Editor content - now renders within layout, not as fixed overlay */}
        <div className="flex-1 flex flex-col overflow-hidden bg-muted min-h-0">
          <EditorTopBar
            name={testDraft.experimentName}
            onRename={(experimentName) => updateTestDraft({ experimentName })}
            pageLabel={`${pageHost}${pagePath}`}
            saveState={changedElsewhere ? "elsewhere" : updateDraftMutation.isPending ? "saving" : hasUnsavedChanges ? "unsaved" : "saved"}
            versions={variants
              .filter((v) => v.id === "A" || v.id === "B")
              .map((v) => ({ ...v, instructionCount: v.id === "B" ? changesOnB : 0 }))}
            currentVersion={currentVariantId}
            onSwitchVersion={handleVariantSwitch}
            device={iframeWidth && iframeWidth <= 430 ? "mobile" : iframeWidth ? "desktop" : "fit"}
            onDevice={(d) => handleViewportChange(d === "mobile" ? VIEWPORT_SIZES[1] : { name: "Best Fit", width: 0, height: 0 })}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onBack={guardedBack}
            launchTabOpen={panelTab === "launch"}
            onLaunchClick={() => setPanelTab("launch")}
          />
          {/* Main Editor Layout: page preview + right panel (Element · Changes · Launch) */}
          <div className="flex-1 flex overflow-hidden relative min-h-0">
            {/* Center - Iframe Preview with horizontal scroll when viewport > container */}
            <div className="flex-1 min-h-0 relative bg-muted overflow-auto">
              {changedElsewhere && (
                <div role="alert" className="mx-7 mt-4 flex items-center gap-3 rounded-lg bg-warning-tint px-3.5 py-2.5 ring-1 ring-warning/25">
                  <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />
                  <p className="grow text-[13px] leading-[18px] text-warning">
                    This draft changed in another tab. Saving here is paused so nothing is overwritten.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="h-7 shrink-0 rounded-lg bg-background px-2.5 text-xs font-medium text-ui-ink ring-1 ring-black/[0.08] hover:bg-muted dark:ring-white/10"
                  >
                    Reload
                  </button>
                </div>
              )}
              {/* Paper "02 Editor": the page sits in a browser frame on the canvas,
                  with one caption line under it. Mobile keeps a device frame. */}
              <div
                className={cn(
                  "flex flex-col items-center gap-3 px-7 pb-3 pt-6",
                  iframeWidth ? "min-h-full" : "h-full",
                )}
                style={iframeWidth ? { minWidth: `${iframeWidth + 56}px` } : { minWidth: "100%" }}
              >
                <div
                  className={cn(
                    "relative flex flex-col overflow-hidden bg-background",
                    iframeWidth && iframeWidth <= 430
                      ? "rounded-[28px] ring-[10px] ring-foreground/85"
                      : "rounded-xl ring-1 ring-ui-hairline shadow-[0_24px_48px_-16px_rgb(150_45_0/0.14)]",
                    !iframeWidth && "w-full grow basis-0 min-h-0",
                  )}
                  style={{
                    width: iframeWidth ? `${iframeWidth}px` : undefined,
                    height: iframeHeight ? `${iframeHeight}px` : undefined,
                    transition: "width 0.3s ease-out, height 0.3s ease-out",
                    flexShrink: 0,
                  }}
                >
                  {!(iframeWidth && iframeWidth <= 430) && (
                    <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-ui-rowline bg-muted/50 px-3" aria-hidden>
                      <span className="size-[9px] rounded-full bg-foreground/15" />
                      <span className="size-[9px] rounded-full bg-foreground/15" />
                      <span className="size-[9px] rounded-full bg-foreground/15" />
                      <span className="flex grow basis-0 justify-center">
                        <span className="flex h-[22px] items-center gap-1.5 rounded-[6px] bg-muted px-3 text-xs leading-4 text-foreground/60">
                          <svg width="10" height="10" viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" opacity=".6" /><path d="M8 11V7a4 4 0 0 1 8 0v4" fill="none" stroke="currentColor" strokeOpacity=".6" strokeWidth="2.4" /></svg>
                          {pageHost}{pagePath}
                        </span>
                      </span>
                    </div>
                  )}
                  <div className="min-h-0 grow basis-0">
                    <IframePreview
                      ref={iframeRef}
                      pageUrl={iframeUrl}
                      device="desktop"
                      zoom={100}
                      onLoad={handleIframeLoad}
                      onBlocked={handleIframeBlocked}
                      onResolvedUrl={handleResolvedUrl}
                      suppressBlockedOverlay={fallbackCause !== null}
                      draftId={draftId}
                      authToken={authToken}
                      viewportWidth={iframeWidth ?? undefined}
                      viewportHeight={iframeHeight ?? undefined}
                      className="!p-0"
                    />
                  </div>
                </div>
                <p className="shrink-0 text-xs leading-4 text-foreground/60">
                  {iframeWidth && iframeWidth <= 430 ? `Mobile layout · ${iframeWidth}px` : "Desktop layout"}
                  {" · "}
                  {currentVariantId === "A" ? "Control is read-only, switch to Variant B to edit" : `Editing version ${currentVariantId} · click any element to change it`}
                </p>
              </div>

              {/* Connection states (Paper system card): one calm card over the
                  canvas. Loading shows the page taking shape; failures say what
                  happened and offer editing on the live site, which saves to the
                  same draft. */}
              {isEditingInNewTab || (fallbackCause && !userDismissedOverlay) || (showLoadingOverlay && !isReady && !fallbackCause) ? (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-muted/85 px-6 backdrop-blur-[2px]">
                  <div className="flex w-full max-w-[400px] flex-col gap-5 rounded-2xl bg-background p-6 shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_24px_48px_-12px_rgb(0_0_0/0.18)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_24px_48px_-12px_rgb(0_0_0/0.6)]">
                    {isEditingInNewTab ? (
                      <>
                        <ConnectionArt state="external" host={pageHost} />
                        <div className="flex flex-col gap-1.5">
                          <h3 className="text-base font-semibold leading-5 text-ui-title">Editing on {pageHost}</h3>
                          <p className="text-[13px] leading-[19px] text-foreground/60">
                            Make your changes in the other tab. Each save comes back to this draft, then launch from here.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2.5 text-[13px] leading-4">
                          <span className={cn("size-1.5 rounded-full", newTabChangeCount > 0 ? "bg-live" : "bg-foreground/25")} aria-hidden />
                          <span className="text-ui-ink" aria-live="polite">
                            {newTabChangeCount > 0
                              ? `${newTabChangeCount} ${newTabChangeCount === 1 ? "change" : "changes"} saved on version B`
                              : "Waiting for your first save"}
                          </span>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              void syncFromServer();
                              setIsEditingInNewTab(false);
                              setUserDismissedOverlay(true);
                              setPanelTab("launch");
                            }}
                            className="h-9 rounded-lg bg-foreground text-[13px] font-medium text-background transition-opacity hover:opacity-90"
                          >
                            Done editing, review and launch
                          </button>
                          <button type="button" onClick={handleOpenInNewWindow} className="h-9 rounded-lg text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] hover:bg-muted dark:ring-white/10">
                            Open the other tab again
                          </button>
                        </div>
                      </>
                    ) : fallbackCause ? (
                      <>
                        <ConnectionArt state="error" host={pageHost} />
                        <div className="flex flex-col gap-1.5">
                          <h3 className="text-base font-semibold leading-5 text-ui-title">
                            {fallbackCause === "embed-blocked" ? `${pageHost} can't open inside Humblytics` : "The editor didn't connect"}
                          </h3>
                          <p className="text-[13px] leading-[19px] text-foreground/60">
                            {fallbackCause === "embed-blocked"
                              ? "Its security settings stop it loading in a frame. Edit on your site instead: the editor opens over the live page, and every change saves to this draft."
                              : "The page loaded, but the Humblytics script on it didn't answer. Check the tracking script is on this page, or edit on your site instead."}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            autoFocus
                            onClick={handleOpenInNewWindow}
                            className="h-9 rounded-lg bg-foreground text-[13px] font-medium text-background transition-opacity hover:opacity-90"
                          >
                            Edit on {pageHost}
                          </button>
                          {fallbackCause === "handshake-failed" && bridge && (
                            <button type="button" onClick={handleRetryConnection} className="h-9 rounded-lg text-[13px] font-medium text-ui-ink ring-1 ring-black/[0.08] hover:bg-muted dark:ring-white/10">
                              Try connecting again
                            </button>
                          )}
                          <button type="button" onClick={() => onBack?.()} className="h-8 text-[13px] text-foreground/60 hover:text-foreground">
                            Back
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <ConnectionArt state="loading" host={pageHost} />
                        <div className="flex flex-col gap-1.5">
                          <h3 className="text-base font-semibold leading-5 text-ui-title">Opening {pageHost}{pagePath}</h3>
                          <ol className="flex flex-col gap-1.5 text-[13px] leading-4" aria-live="polite">
                            <StepLine done={Boolean(bridge)} active={!bridge} label="Loading your page" />
                            <StepLine done={false} active={Boolean(bridge)} label="Connecting the editor" />
                          </ol>
                        </div>
                        <div className="flex items-center justify-between gap-3 border-t border-ui-rowline pt-4">
                          <button type="button" onClick={handleOpenInNewWindow} className="text-[13px] font-medium text-ui-ink hover:underline">
                            Edit on {pageHost} instead
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowLoadingOverlay(false);
                              setUserDismissedOverlay(true);
                              clearConnectionTimers();
                            }}
                            className="text-[13px] text-foreground/60 hover:text-foreground"
                          >
                            Hide
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : null}

              {/* After Hide, keep a quiet way out while the editor is not connected */}
              {!isReady && userDismissedOverlay && !showLoadingOverlay && !isEditingInNewTab && (
                <div className="absolute left-1/2 top-4 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-background px-3.5 py-2 shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_8px_24px_-8px_rgb(0_0_0/0.2)]">
                  <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                  <span className="text-[13px] text-ui-ink">Editor not connected</span>
                  <button type="button" onClick={handleOpenInNewWindow} className="text-[13px] font-medium text-ui-ink hover:underline">
                    Edit on {pageHost}
                  </button>
                </div>
              )}
            </div>

            {/* AI Optimize modal - when user clicks AI Optimize from HoverActions */}
            {aiOptimizeElementData && bridge && (
              <AIVaryModal
                elementData={aiOptimizeElementData}
                bridge={bridge}
                onClose={() => setAiOptimizeElementData(null)}
              />
            )}

            <aside className="flex w-[380px] shrink-0 flex-col border-l border-border bg-background" aria-label="Editor panel">
              <div className="flex h-11 shrink-0 items-end gap-[18px] border-b border-border px-5" role="tablist">
                {([
                  { id: "element", label: "Element" },
                  { id: "changes", label: "Changes", count: trackedInstructions.length },
                  { id: "launch", label: "Launch" },
                ] as Array<{ id: PanelTab; label: string; count?: number }>).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={panelTab === tab.id}
                    onClick={() => setPanelTab(tab.id)}
                    className={cn(
                      "flex h-11 items-center gap-1.5 text-[13px] font-medium leading-4 transition-colors",
                      panelTab === tab.id ? "text-ui-title shadow-[inset_0_-2px_0_var(--color-foreground)]" : "text-foreground/60 hover:text-foreground",
                    )}
                  >
                    {tab.label}
                    {tab.count ? (
                      <span className="flex h-4 items-center rounded-[4px] bg-muted px-[5px] text-xs leading-4 text-foreground/60">{tab.count}</span>
                    ) : null}
                  </button>
                ))}
              </div>
              {panelTab === "element" && (
                aiSidebar.editorElement && bridge ? (
                  <div className="flex min-h-0 grow flex-col">
                    <EditorElementPanel
                      element={aiSidebar.editorElement}
                      bridge={bridge}
                      versionId={currentVariantId}
                      controlText={controlTextBySelector.current.get(aiSidebar.editorElement.selector) ?? null}
                      hasChange={trackedInstructions.some((i) => i.selector === aiSidebar.editorElement?.selector)}
                      savedText={(() => {
                        const last = [...trackedInstructions].reverse().find(
                          (i) => i.selector === aiSidebar.editorElement?.selector && i.operation === "text",
                        );
                        return last ? instructionTextValue(last.value) ?? null : null;
                      })()}
                      onResetToControl={() => {
                        const selector = aiSidebar.editorElement?.selector;
                        syncInstructions(trackedInstructions.filter((i) => i.selector !== selector));
                      }}
                      onDeselect={() => aiSidebar.setEditorElement(null)}
                      pageDaily={pageDaily}
                    />
                  </div>
                ) : (
                  <div className="flex grow flex-col gap-4 px-5 pt-5">
                    <div className="flex flex-col gap-1">
                      <h2 className="text-base font-semibold leading-5 text-ui-title">
                        {currentVariantId === "A" ? "Control is the page as it is today" : `Change anything on version ${currentVariantId}`}
                      </h2>
                      <p className="text-[13px] leading-[19px] text-foreground/60">
                        {currentVariantId === "A"
                          ? "Visitors in Control see it unchanged. Make your change on version B."
                          : "Click a headline, button or image on the page, change it here, and it saves on its own."}
                      </p>
                    </div>
                    {currentVariantId === "A" ? (
                      <button
                        type="button"
                        onClick={() => handleVariantSwitch("B")}
                        className="h-[34px] w-fit rounded-lg bg-foreground px-3.5 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
                      >
                        Edit Variant B
                      </button>
                    ) : (
                      <ul className="flex flex-col gap-2 text-[13px] leading-[19px] text-foreground/70">
                        <li className="flex gap-2.5"><span className="font-medium tabular-nums text-ui-title">1</span>Click the element you want to test</li>
                        <li className="flex gap-2.5"><span className="font-medium tabular-nums text-ui-title">2</span>Rewrite it, restyle it or hide it</li>
                        <li className="flex gap-2.5"><span className="font-medium tabular-nums text-ui-title">3</span>Open Launch to check the goal and start</li>
                      </ul>
                    )}
                    <p className="border-t border-ui-rowline pt-3 text-xs leading-[17px] text-foreground/60">
                      Page looks off here, or need a logged-in view?{" "}
                      <button type="button" onClick={handleOpenInNewWindow} className="font-medium text-ui-ink hover:underline">
                        Edit on {pageHost}
                      </button>{" "}
                      instead. Changes save to this draft.
                    </p>
                  </div>
                )
              )}
              {panelTab === "changes" && (
                <EditorChangesPanel
                  versionLabel={`Variant ${currentVariantId}`}
                  instructions={trackedInstructions}
                  onEdit={(instruction) => {
                    bridge?.send({ type: "SCROLL_TO_ELEMENT", payload: { selector: instruction.selector } });
                    bridge?.send({ type: "SELECTION_SET", payload: { selector: instruction.selector } });
                    aiSidebar.openEditorTab({ selector: instruction.selector, tagName: "", textContent: "", computedStyles: {} });
                  }}
                  onRemove={(id) => syncInstructions(trackedInstructions.filter((instruction) => instruction.id !== id))}
                  onClearAll={() => syncInstructions([])}
                />
              )}
              {panelTab === "launch" && (
                <LaunchSettingsPanel
                  draft={testDraft}
                  onChange={updateTestDraft}
                  pageDaily={pageDaily}
                  changesCount={changesOnB}
                  suggestedGoalIds={suggestedGoals}
                  onBack={() => setPanelTab("element")}
                  onLaunch={handleLaunch}
                  launching={isLaunching}
                  atLimit={atLimit}
                />
              )}
            </aside>
          </div>

          {/* Unsaved Changes Dialog */}
          <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes. What would you like to do?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex gap-2">
                <AlertDialogCancel onClick={() => setShowUnsavedDialog(false)}>
                  Keep editing
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    setShowUnsavedDialog(false);
                    onBack?.();
                  }}
                >
                  Discard & Go back
                </AlertDialogAction>
                <AlertDialogAction onClick={handleSaveAndGoBack}>
                  Save & Go back
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Review Before Save Sheet */}
          <Sheet open={showReviewBeforeSave} onOpenChange={setShowReviewBeforeSave}>
            <SheetContent className="w-[400px]">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <HugeiconsIcon icon={Clock02Icon} size={20} className="text-primary" />
                  Review changes
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {trackedInstructions.length} change{trackedInstructions.length !== 1 ? 's' : ''} will be saved.
                </p>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {trackedInstructions.map((inst, i) => (
                    <div key={inst.id || i} className="p-2 border border-border rounded-md text-xs">
                      <span className="font-medium text-foreground">{inst.operation}</span>
                      <span className="text-muted-foreground ml-2 font-mono truncate">{inst.selector?.substring(0, 40)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReviewBeforeSave(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConfirmSaveFromReview}
                    className="flex-1 gap-1.5"
                  >
                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} />
                    Confirm save
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

        </div>
      </EditorProvider>
  );
}

/** One step of the loading card: done (check), active (spinner) or waiting. */
function StepLine({ done, active, label }: { done: boolean; active: boolean; label: string }) {
  return (
    <li className={cn("flex items-center gap-2", done || active ? "text-ui-ink" : "text-foreground/40")}>
      <span className="flex size-4 items-center justify-center" aria-hidden>
        {done ? (
          <svg width="14" height="14" viewBox="0 0 24 24"><path d="m5 12 5 5 9-10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        ) : active ? (
          <span className="size-3 rounded-full border-[1.5px] border-foreground/20 border-t-foreground/70 animate-spin motion-reduce:animate-none" />
        ) : (
          <span className="size-1.5 rounded-full bg-foreground/25" />
        )}
      </span>
      {label}
      <span className="sr-only">{done ? " done" : active ? " in progress" : ""}</span>
    </li>
  );
}

/** A miniature browser: the page taking shape (loading), a blocked frame (error) or a page opened elsewhere (external). */
function ConnectionArt({ state, host }: { state: "loading" | "error" | "external"; host: string }) {
  return (
    <div className="overflow-hidden rounded-xl bg-muted/60 ring-1 ring-ui-hairline" aria-hidden>
      <div className="flex h-6 items-center gap-1 border-b border-ui-rowline px-2.5">
        <span className="size-1.5 rounded-full bg-foreground/15" />
        <span className="size-1.5 rounded-full bg-foreground/15" />
        <span className="size-1.5 rounded-full bg-foreground/15" />
        <span className="mx-auto truncate rounded-[4px] bg-background/80 px-2 text-[10px] leading-4 text-foreground/50">{host}</span>
      </div>
      <div className="relative flex h-[104px] flex-col items-center justify-center gap-2 px-6">
        {state === "loading" && (
          <>
            <span className="h-3 w-3/5 rounded bg-foreground/10 animate-pulse motion-reduce:animate-none" />
            <span className="h-2 w-2/5 rounded bg-foreground/[0.07] animate-pulse motion-reduce:animate-none [animation-delay:150ms]" />
            <span className="mt-1 flex gap-1.5">
              <span className="h-4 w-14 rounded-[5px] bg-primary/35 animate-pulse motion-reduce:animate-none [animation-delay:300ms]" />
              <span className="h-4 w-14 rounded-[5px] bg-foreground/[0.08] animate-pulse motion-reduce:animate-none [animation-delay:300ms]" />
            </span>
          </>
        )}
        {state === "error" && (
          <span className="flex size-9 items-center justify-center rounded-full bg-warning-tint text-warning ring-1 ring-warning/20">
            <svg width="16" height="16" viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
          </span>
        )}
        {state === "external" && (
          <span className="flex size-9 items-center justify-center rounded-full bg-background text-ui-ink ring-1 ring-ui-hairline">
            <svg width="16" height="16" viewBox="0 0 24 24"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Inner wrapper that checks for existing AISidebarContext
 * 
 * NOTE: We intentionally do NOT create a nested AIPageContextProvider here.
 * When inside DashboardLayout, we want EditorShellInner to update the OUTER
 * AIPageContext (from DashboardLayout) so that AISidebar can access the
 * split test editor context. Creating a nested provider would isolate the
 * context and AISidebar wouldn't see the updates.
 * 
 * EditorShellInner calls setSplitTestEditorContext() to update the outer context.
 */
function EditorShellWithContext(props: EditorShellProps) {
  // No nested AIPageContextProvider - let EditorShellInner update the outer context
  return <EditorShellInner {...props} />;
}

/**
 * Main EditorShell component - wraps inner component with providers
 * Conditionally provides AISidebarProvider if not already present from parent
 */
export function EditorShell(props: EditorShellProps) {
  // Check if we're already inside an AISidebarProvider (from DashboardLayout)
  const existingSidebar = useAISidebarSafe();
  // Check if we're already inside an AIPageContext (from DashboardLayout)
  const existingPageContext = useAIPageContextSafe();
  
  // If already inside both providers (from DashboardLayout), don't wrap again
  // EditorShellInner will update the existing context via setSplitTestEditorContext
  if (existingSidebar && existingPageContext) {
    return <EditorShellWithContext {...props} />;
  }
  
  // Standalone mode - need to provide our own providers
  // This includes AIPageContextProvider so EditorShellInner can update it
  return (
    <AISidebarProvider defaultOpen={false}>
      <AIPageContextProvider
        value={{
          context: {
            pageTitle: "Split Test Editor",
            currentPage: props.pageUrl,
            propertyId: props.draftId,
            filters: [],
            period: "LAST_7_DAYS",
            metrics: {},
            appExperience: {
              routeId: "split_test_editor",
              displayName: "Split Test Editor",
            },
            // splitTestEditorContext will be set by EditorShellInner
          },
        }}
      >
        <EditorShellWithContext {...props} />
      </AIPageContextProvider>
    </AISidebarProvider>
  );
}

export default EditorShell;
